<script lang="ts" setup>
import { useElementHover } from '@vueuse/core'
import { ChevronRightIcon } from 'mdi-vue3'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'

import { ABadge, AMenu, type Testable } from '../../../main'
import type { ListItemInterface } from '.'

defineOptions({ inheritAttrs: false })

type ListItemModel = never

type ListItemEmits = never

type ListItemExpose = never

type ListItemSlots = never

export type ListItemTypes = {
  model: ListItemModel
  properties: ListItemProperties
  emits: ListItemEmits
  expose: ListItemExpose
  slots: ListItemSlots
}

export interface ListItemProperties extends Testable {
  item: ListItemInterface
  active?: boolean
  collapsed?: boolean
  expanded?: boolean
  hover?: boolean
  level?: number
}

const properties = withDefaults(defineProps<ListItemProperties>(), { level: 0, expanded: false })

onMounted(() => {
  if (properties.item.expanded) {
    isExpanded.value = properties.item.expanded
  }
})

const emit = defineEmits<{
  (event: 'click', item: ListItemInterface): void
  (event: 'openSub'): void
  (event: 'hover', value: boolean): void
}>()

const isExpanded = defineModel<boolean>({ default: false })

const isClickable = computed(() => {
  return !properties.item.disabled && (properties.item.to || properties.item.click || properties.item.children)
})

const isSubItemOpen = ref<boolean>(false)
const itemContainer = ref()
const isHovered = useElementHover(itemContainer, { delayEnter: 400, delayLeave: 20_000 })

function onClick(event: MouseEvent | KeyboardEvent, item: ListItemInterface) {
  if (item.click) {
    item.click(item)
  }

  emit('click', item)
  item.active = !item.active
}

watch(
  () => properties.hover && isHovered.value,
  (hovered) => {
    isSubItemOpen.value = hovered ? true : false
    emit('hover', hovered)
  },
)

const padding = computed(() => {
  return properties.level ? `${properties.level * 20}px` : undefined
})

//
</script>

<template>
  <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
  <div class="atabix-list-item-container">
    <div
      class="atabix-list-item"
      :class="{
        'atabix-list-item__seperator': item.seperator,
        'atabix-list-item__title-bold': item.bold,
        'atabix-list-item__item--clickable': isClickable,
        'atabix-list-item__item--disabled': item.disabled,
        'atabix-list-item__item--active': item.active,
        'atabix-list-item__item--expanded': isExpanded,
        'atabix-list-item__item--collapsed': collapsed,
      }"
      :data-test
      :role="isClickable && 'button'"
      tabindex="0"
      @click="($event) => isClickable && onClick($event, item)"
      @keydown="($event: KeyboardEvent) => isClickable && $event.key === 'Enter' && onClick($event, item)"
    >
      <template v-if="!item.seperator">
        <component
          :is="typeof item.to === 'string' ? 'a' : RouterLink"
          v-if="item.to && !item.children"
          :active-class="'router-link-active'"
          class="atabix-list-item__item"
          :class="{ 'router-link-active': item.active }"
          :data-test="item.dataTest"
          :href="typeof item.to === 'string' ? item.to : undefined"
          :to="item.to"
        >
          <div class="atabix-list-item__item-container" :style="{ paddingLeft: padding }">
            <component :is="item.icon" v-if="item.icon && (item.iconPosition === 'left' || item.iconPosition === undefined)" />
            <span v-if="!collapsed" class="atabix-list-item__text" :data-content="item.title">{{ item.title }}</span>
            <component :is="item.icon" v-if="item.icon && item.iconPosition === 'right'" />
          </div>
          <ABadge v-if="item.badge" class="font-inter" variant="destructive">{{ item.badge }}</ABadge>
        </component>

        <template v-else>
          <div
            class="atabix-list-item__item focus:bg-background-selected"
            :class="{ 'atabix-list-item__item-selected': isExpanded }"
            :data-test="item.dataTest"
            role="button"
            :style="{ paddingLeft: item.children ? padding : undefined }"
            tabindex="0"
            @click="isExpanded = !isExpanded"
            @keydown="isExpanded = !isExpanded"
          >
            <div ref="itemContainer" class="atabix-list-item__item-container">
              <ChevronRightIcon v-if="!collapsed && item.children?.length" class="atabix-list-item__item-button" />
              <component :is="item.icon" v-if="item.icon && (item.iconPosition === 'left' || item.iconPosition === undefined)" class="size-4" />

              <template v-if="!collapsed">
                <router-link v-if="item.to" class="atabix-list-item__text" :data-content="item.title" :to="item.to" @click.stop="() => {}">
                  {{ item.title }}
                </router-link>
                <span v-else class="atabix-list-item__text" :data-content="item.title">{{ item.title }}</span>
              </template>

              <component :is="item.icon" v-if="item.icon && item.iconPosition === 'right'" />
            </div>
            <ABadge v-if="item.badge" class="font-inter" variant="destructive">{{ item.badge }}</ABadge>
          </div>
        </template>
      </template>
    </div>

    <div v-if="item.children?.length" v-show="isExpanded && !collapsed">
      <AMenu :collapsed="collapsed" :items="item.children" :level="level + 1" />
    </div>
  </div>
</template>

<style lang="scss">
.atabix-list-item-container {
  @apply w-full;
}

.atabix-list-item {
  @apply flex;
  @apply flex-row;
  @apply text-base;
  @apply gap-1;
  @apply break-keep;
  @apply font-normal;
  @apply whitespace-nowrap;
  @apply cursor-default;
  @apply w-full;
  @apply justify-between;
  @apply px-2;

  svg {
    @apply h-5;
  }
}

.atabix-list-item__item--collapsed {
  &.atabix-list-item__item {
    @apply w-8;
  }
}

.atabix-list-item__title-bold {
  @apply font-semibold;
  @apply text-sm;
}

.atabix-list-item__item--active {
  .atabix-list-item--part {
    @apply transition-all;
    @apply rounded-md;
    @apply bg-background-selected;
    @apply text-foreground-accent;
  }
}

.atabix-list-item__item--disabled {
  .atabix-list-item__item {
    @apply cursor-not-allowed;
    @apply bg-background-disabled;
  }
}

.atabix-list-item__item--clickable,
.atabix-list-item__item.router-link-active {
  @apply cursor-pointer;
  @apply font-bold;

  &:hover {
    .atabix-list-item__item {
      @apply transition-all;
      @apply text-foreground-accent;
      @apply rounded-md;
      @apply bg-background-selected;
    }
  }
}

.atabix-list-item__seperator {
  height: 1px;
  width: 100%;
  @apply bg-border;
}

.atabix-list-item__item {
  @apply flex;
  @apply flex-row;
  @apply items-center;
  @apply font-normal;
  @apply justify-between;
  @apply gap-2;
  @apply p-2;
  @apply w-full;

  &.router-link-active {
    @apply transition-all;
    @apply font-bold;
    @apply rounded-md;
    @apply bg-background-selected;
    @apply text-foreground-accent;
  }
}

.atabix-list-item__item--collapsed {
  a,
  .atabix-list-item__item {
    @apply justify-center;
  }
}

.atabix-list-item__item-container {
  @apply truncate;
  @apply flex;
  @apply gap-2;
  @apply items-center;
  @apply justify-center;
}

.atabix-list-item__item-selected {
  @apply rounded-md;
  // @apply focus:bg-background-selected;
}

.atabix-list-item__item-button {
  @apply size-5;
  @apply transition-all duration-300;
}

.atabix-list-item__item--expanded {
  @apply mb-1;

  .atabix-list-item__item-button {
    @apply rotate-180;
  }

  .atabix-list-item__item-container {
    .atabix-list-item__item-button {
      @apply rotate-90;
    }
  }
}

.atabix-list-item__text::before {
  content: attr(data-content);
  display: block;
  font-weight: bold;
  height: 0px;
  visibility: hidden;
}
</style>
